import { Product } from 'types/product';
import { ThemeConfig } from 'types/storefront';
import { SOCIAL_NETWORKS } from 'config/socialNetworks';
import { ROUTES } from 'config/routes';
import { resolveSiteName, resolveStoreMediaUrl } from 'utils/themeDisplay';
import { schemaAvailability } from 'utils/stockLevel';

/** URL canónica: origen + ruta, sin query ni hash. */
export const buildCanonicalUrl = (pathname: string, origin = window.location.origin): string => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
  return `${origin}${path}`;
};

/** Texto plano y recortado para meta description (las descripciones de producto pueden traer HTML). */
export const toMetaDescription = (value: string | undefined | null, maxLength: number): string => {
  const text = (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};

const omitEmpty = <T extends Record<string, unknown>>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) =>
      Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== ''
    )
  ) as Partial<T>;

export const buildOrganizationJsonLd = (theme: ThemeConfig | null | undefined, origin = window.location.origin) => {
  const contact = theme?.contact;
  const sameAs = SOCIAL_NETWORKS.map((network) => contact?.[network.key]?.trim()).filter(
    (url): url is string => Boolean(url)
  );
  return omitEmpty({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: resolveSiteName(theme),
    url: `${origin}/`,
    logo: resolveStoreMediaUrl(theme?.logoUrl) || undefined,
    telephone: contact?.phone?.trim() || undefined,
    email: contact?.email?.trim() || undefined,
    address: contact?.address?.trim() || undefined,
    sameAs,
  });
};

export const buildWebSiteJsonLd = (theme: ThemeConfig | null | undefined, origin = window.location.origin) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: resolveSiteName(theme),
  url: `${origin}/`,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${origin}${ROUTES.SEARCH}?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export const buildProductJsonLd = (product: Product, siteName: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.imageUrl,
  sku: product.sku,
  brand: product.brandName ? { '@type': 'Brand', name: product.brandName } : undefined,
  offers: {
    '@type': 'Offer',
    price: product.price,
    priceCurrency: product.currency || 'ARS',
    availability: schemaAvailability(product),
    seller: { '@type': 'Organization', name: siteName },
  },
});

/** Migas visibles a JSON-LD: el ítem sin enlace (la página actual) usa la URL canónica. */
export const buildBreadcrumbJsonLdFromItems = (
  items: { label: string; to?: string }[],
  currentUrl: string,
  origin = window.location.origin
) =>
  buildBreadcrumbJsonLd(
    items.map((item) => ({
      name: item.label,
      url: item.to ? `${origin}${item.to}` : currentUrl,
    }))
  );

export const buildBreadcrumbJsonLd = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});
